import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 700 }]
});

// ─── Design tokens ───────────────────────────────────────────────
const C = {
  primary:      '#1565C0',
  primaryDark:  '#0D47A1',
  primaryLight: '#E3F2FD',
  sectionBg:    '#1976D2',
  subtotalBg:   '#BBDEFB',
  dateBg:       '#F5F5F5',
  altRow:       '#F5F8FF',
  white:        '#FFFFFF',
  dark:         '#1A1A2E',
  border:       '#B0BEC5',
  green:        '#1B5E20',
  red:          '#B71C1C',
  gray:         '#546E7A',
};

const styles = StyleSheet.create({
  page: { padding: '16pt 18pt', fontSize: 7, color: C.dark, backgroundColor: C.white },

  logo:        { width: '100%', height: 64, marginBottom: 2 },
  headerRule:  { height: 2, backgroundColor: C.primary, marginBottom: 4 },
  reportTitle: { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primaryDark, marginBottom: 1 },
  doctorTitle: { fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primary, marginBottom: 1 },
  reportSub:   { fontSize: 7, textAlign: 'center', color: C.gray, marginBottom: 5 },

  // Section strip
  sectionStrip: {
    backgroundColor: C.sectionBg, paddingVertical: 5, paddingHorizontal: 8,
    marginTop: 10, borderRadius: 2,
  },
  sectionStripText: { fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: C.white },

  // Table header (2-col for summary)
  tableHead2: { flexDirection: 'row', backgroundColor: C.primary, borderRadius: 2, marginTop: 4 },
  th2: {
    flex: 1, color: C.white, fontFamily: 'Roboto', fontWeight: 700, fontSize: 7,
    paddingVertical: 4, paddingHorizontal: 6, textAlign: 'center',
    borderRightWidth: 0.5, borderRightColor: '#90CAF9',
  },

  // Date block
  dateBar: {
    backgroundColor: C.dateBg, borderLeftWidth: 3, borderLeftColor: C.primary,
    paddingVertical: 3, paddingHorizontal: 6, marginTop: 6,
  },
  dateBarText: { fontSize: 7.5, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark },

  // Summary rows
  summRow:     { flexDirection: 'row', borderBottomWidth: 0.3, borderBottomColor: C.border },
  summRowEven: { backgroundColor: C.white },
  summRowOdd:  { backgroundColor: C.altRow },
  summCell:    { flex: 1, fontSize: 7, paddingVertical: 3, paddingHorizontal: 6 },
  summAmt:     { flex: 1, fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, paddingVertical: 3, paddingHorizontal: 6, textAlign: 'right', color: C.primaryDark },

  // Subtotal
  subtotalRow: {
    flexDirection: 'row', backgroundColor: C.subtotalBg,
    borderTopWidth: 0.5, borderTopColor: C.primary,
    paddingVertical: 3, paddingHorizontal: 6, marginBottom: 4,
  },
  subtotalLabel: { flex: 1, fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right' },
  subtotalAmt:   { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, paddingLeft: 10, minWidth: 80, textAlign: 'right' },

  // Section total
  sectionTotalRow: {
    flexDirection: 'row', backgroundColor: C.primaryLight,
    borderTopWidth: 1, borderTopColor: C.primaryDark,
    paddingVertical: 4, paddingHorizontal: 6, marginTop: 2, marginBottom: 8, borderRadius: 2,
  },
  sectionTotalLabel: { flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right' },
  sectionTotalAmt:   { fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, paddingLeft: 10, minWidth: 80, textAlign: 'right' },

  // Grand total
  grandBox: {
    marginTop: 14, borderWidth: 1.5, borderColor: C.primaryDark,
    backgroundColor: C.primaryDark, borderRadius: 4,
    flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 12, alignItems: 'center',
  },
  grandLabel: { flex: 1, fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, color: C.white },
  grandAmt:   { fontSize: 14, fontFamily: 'Roboto', fontWeight: 700, color: '#FFD54F' },

  // Footer
  footer: {
    position: 'absolute', bottom: 10, left: 18, right: 18,
    flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: C.border, paddingTop: 4,
  },
  footerLeft:  { flex: 1, fontSize: 6, color: C.gray },
  footerRight: { fontSize: 6, color: C.gray, textAlign: 'right' },

  emptyText: { fontSize: 6.5, color: C.gray, fontStyle: 'italic', padding: 6 },
});

// ─── Helpers ──────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt)) return '-';
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}
function fmt(n)    { return (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtINR(n) { return `₹ ${fmt(n)}`; }
function nowStr()  {
  const d = new Date();
  return `${fmtDate(d)}  ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

const groupByDate = (arr) => {
  if (!Array.isArray(arr)) return {};
  return arr.reduce((g, item) => {
    const d = fmtDate(item?.date);
    if (!g[d]) g[d] = [];
    g[d].push(item);
    return g;
  }, {});
};

// ─── Main Component ───────────────────────────────────────────────
export const DoctorLedgerSummaryReportPdf = ({ data }) => {
  const groupedData    = data?.groupedData       || {};
  const opdBillingData = data?.opdBillingData?.data || [];
  const ipdBillingData = data?.ipdBillingData?.data || [];

  const opdBillGrouped = groupByDate(opdBillingData);
  const ipdGrouped     = groupByDate(ipdBillingData);

  // Use service_items.amount / disc_amt (per-service values) — service_items is unwound so it's a single object.
  // Bill-level disc_amt repeats on every unwound row: count it once per bill _id.
  const sumItems = (arr) => {
    const seen = {};
    return arr.reduce((t, i) => {
      const bill = parseFloat(i.service_items?.amount) || 0;
      let disc = parseFloat(i.service_items?.disc_amt) || 0;
      if (i?._id && !seen[i._id]) {
        seen[i._id] = true;
        disc += parseFloat(i?.disc_amt) || 0;
      }
      return { bill: t.bill + bill, disc: t.disc + disc, net: t.net + (bill - disc) };
    }, { bill: 0, disc: 0, net: 0 });
  };

  const addTotals = (a, b) => ({ bill: a.bill + b.bill, disc: a.disc + b.disc, net: a.net + b.net });
  const ZERO = { bill: 0, disc: 0, net: 0 };

  const opdConsultancyTotal = Object.values(groupedData).reduce((t, g) => {
    const bill = parseFloat(g.total_reg_amount) || 0;
    const disc = parseFloat(g.total_disc_amount) || 0;
    return { bill: t.bill + bill, disc: t.disc + disc, net: t.net + (bill - disc) };
  }, { ...ZERO });
  const opdBillingTotal = Object.values(opdBillGrouped).reduce((t, rows) => addTotals(t, sumItems(rows)), { ...ZERO });
  const ipdBillingTotal = Object.values(ipdGrouped).reduce((t, rows) => addTotals(t, sumItems(rows)), { ...ZERO });

  const grandBill = opdConsultancyTotal.bill + opdBillingTotal.bill + ipdBillingTotal.bill;
  const grandDisc = opdConsultancyTotal.disc + opdBillingTotal.disc + ipdBillingTotal.disc;
  const grandNet  = grandBill - grandDisc;

  const doctorName =
    Object.values(groupedData)[0]?.consultant_name ||
    opdBillingData[0]?.patient?.consultant?.name  ||
    ipdBillingData[0]?.patient?.consultant?.name  || 'Doctor';

  const printTime = nowStr();

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1000, backgroundColor: 'white' }}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Doctor Ledger Summary Report">
          <PDFPage style={styles.page} size="A4" orientation="portrait" wrap>

            {/* HEADER */}
            <PDFImage src="/logo_Hospital_m.jpeg" style={styles.logo} />
            <PDFView style={styles.headerRule} />
            <PDFText style={styles.reportTitle}>DOCTOR LEDGER — SUMMARY REPORT</PDFText>
            <PDFText style={styles.doctorTitle}>{doctorName}</PDFText>
            <PDFText style={styles.reportSub}>Printed on: {printTime}</PDFText>
            <PDFView style={[styles.headerRule, { height: 0.5, marginBottom: 4 }]} />

            {/* ── OPD CONSULTANCY ─────────────────────────── */}
            <PDFView style={styles.sectionStrip}>
              <PDFText style={styles.sectionStripText}>OPD CONSULTANCY</PDFText>
            </PDFView>
            <PDFView style={styles.tableHead2}>
              <PDFText style={styles.th2}>Date</PDFText>
              <PDFText style={styles.th2}>Patients</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Bill Amt.</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Disc.</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Net Amt.</PDFText>
            </PDFView>

            {Object.keys(groupedData).length > 0 ? (
              Object.keys(groupedData).map((groupDate, i) => {
                const g = groupedData[groupDate];
                const bill = g.total_reg_amount || 0;
                const disc = g.total_disc_amount || 0;
                return (
                  <PDFView key={groupDate} style={[styles.summRow, i % 2 === 0 ? styles.summRowEven : styles.summRowOdd]}>
                    <PDFText style={styles.summCell}>{fmtDate(groupDate)}</PDFText>
                    <PDFText style={[styles.summCell, { textAlign: 'center' }]}>
                      {g.quantity} patient{g.quantity !== 1 ? 's' : ''}
                    </PDFText>
                    <PDFText style={[styles.summCell, { textAlign: 'right' }]}>{fmtINR(bill)}</PDFText>
                    <PDFText style={[styles.summCell, { textAlign: 'right', color: disc > 0 ? C.red : C.dark }]}>{fmtINR(disc)}</PDFText>
                    <PDFText style={styles.summAmt}>{fmtINR(bill - disc)}</PDFText>
                  </PDFView>
                );
              })
            ) : (
              <PDFView><PDFText style={styles.emptyText}>No consultancy data available.</PDFText></PDFView>
            )}

            <PDFView style={styles.sectionTotalRow}>
              <PDFText style={styles.sectionTotalLabel}>
                OPD Consultancy — Bill: {fmtINR(opdConsultancyTotal.bill)}   Disc: {fmtINR(opdConsultancyTotal.disc)}   Net:
              </PDFText>
              <PDFText style={styles.sectionTotalAmt}>{fmtINR(opdConsultancyTotal.net)}</PDFText>
            </PDFView>

            {/* ── OPD BILLING ─────────────────────────────── */}
            <PDFView style={styles.sectionStrip}>
              <PDFText style={styles.sectionStripText}>OPD BILLING</PDFText>
            </PDFView>
            <PDFView style={styles.tableHead2}>
              <PDFText style={styles.th2}>Date</PDFText>
              <PDFText style={styles.th2}>Service</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Bill Amt.</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Disc.</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Net Amt.</PDFText>
            </PDFView>

            {Object.keys(opdBillGrouped).length > 0 ? (
              Object.keys(opdBillGrouped).map((groupDate, gi) => {
                const rows = opdBillGrouped[groupDate];
                const dayTotal = sumItems(rows);
                return (
                  <PDFView key={groupDate}>
                    <PDFView style={styles.dateBar}>
                      <PDFText style={styles.dateBarText}>{groupDate}</PDFText>
                    </PDFView>
                    {rows.map((item, i) => {
                      const bill = item.service_items?.amount || 0;
                      const disc = item.service_items?.disc_amt || 0;
                      return (
                        <PDFView key={i} style={[styles.summRow, i % 2 === 0 ? styles.summRowEven : styles.summRowOdd]}>
                          <PDFText style={styles.summCell}>{groupDate}</PDFText>
                          <PDFText style={styles.summCell}>
                            {item?.serviceDetail?.service_name || item?.serviceDetail?.type || 'Service'}
                          </PDFText>
                          <PDFText style={[styles.summCell, { textAlign: 'right' }]}>{fmtINR(bill)}</PDFText>
                          <PDFText style={[styles.summCell, { textAlign: 'right', color: disc > 0 ? C.red : C.dark }]}>{fmtINR(disc)}</PDFText>
                          <PDFText style={styles.summAmt}>{fmtINR(bill - disc)}</PDFText>
                        </PDFView>
                      );
                    })}
                    <PDFView style={styles.subtotalRow}>
                      <PDFText style={styles.subtotalLabel}>
                        Subtotal ({groupDate}) — Bill: {fmtINR(dayTotal.bill)}   Disc: {fmtINR(dayTotal.disc)}   Net:
                      </PDFText>
                      <PDFText style={styles.subtotalAmt}>{fmtINR(dayTotal.net)}</PDFText>
                    </PDFView>
                  </PDFView>
                );
              })
            ) : (
              <PDFView><PDFText style={styles.emptyText}>No OPD billing data available.</PDFText></PDFView>
            )}

            <PDFView style={styles.sectionTotalRow}>
              <PDFText style={styles.sectionTotalLabel}>
                OPD Billing — Bill: {fmtINR(opdBillingTotal.bill)}   Disc: {fmtINR(opdBillingTotal.disc)}   Net:
              </PDFText>
              <PDFText style={styles.sectionTotalAmt}>{fmtINR(opdBillingTotal.net)}</PDFText>
            </PDFView>

            {/* ── IPD BILLING ─────────────────────────────── */}
            <PDFView style={styles.sectionStrip}>
              <PDFText style={styles.sectionStripText}>IPD BILLING</PDFText>
            </PDFView>
            <PDFView style={styles.tableHead2}>
              <PDFText style={styles.th2}>Date</PDFText>
              <PDFText style={styles.th2}>Service</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Bill Amt.</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Disc.</PDFText>
              <PDFText style={[styles.th2, { textAlign: 'right' }]}>Net Amt.</PDFText>
            </PDFView>

            {Object.keys(ipdGrouped).length > 0 ? (
              Object.keys(ipdGrouped).map((groupDate, gi) => {
                const rows = ipdGrouped[groupDate];
                const dayTotal = sumItems(rows);
                return (
                  <PDFView key={groupDate}>
                    <PDFView style={styles.dateBar}>
                      <PDFText style={styles.dateBarText}>{groupDate}</PDFText>
                    </PDFView>
                    {rows.map((item, i) => {
                      const bill = item.service_items?.amount || 0;
                      const disc = item.service_items?.disc_amt || 0;
                      return (
                        <PDFView key={i} style={[styles.summRow, i % 2 === 0 ? styles.summRowEven : styles.summRowOdd]}>
                          <PDFText style={styles.summCell}>{groupDate}</PDFText>
                          <PDFText style={styles.summCell}>
                            {item?.serviceDetail?.service_name || item?.serviceDetail?.type || 'Service'}
                          </PDFText>
                          <PDFText style={[styles.summCell, { textAlign: 'right' }]}>{fmtINR(bill)}</PDFText>
                          <PDFText style={[styles.summCell, { textAlign: 'right', color: disc > 0 ? C.red : C.dark }]}>{fmtINR(disc)}</PDFText>
                          <PDFText style={styles.summAmt}>{fmtINR(bill - disc)}</PDFText>
                        </PDFView>
                      );
                    })}
                    <PDFView style={styles.subtotalRow}>
                      <PDFText style={styles.subtotalLabel}>
                        Subtotal ({groupDate}) — Bill: {fmtINR(dayTotal.bill)}   Disc: {fmtINR(dayTotal.disc)}   Net:
                      </PDFText>
                      <PDFText style={styles.subtotalAmt}>{fmtINR(dayTotal.net)}</PDFText>
                    </PDFView>
                  </PDFView>
                );
              })
            ) : (
              <PDFView><PDFText style={styles.emptyText}>No IPD billing data available.</PDFText></PDFView>
            )}

            <PDFView style={styles.sectionTotalRow}>
              <PDFText style={styles.sectionTotalLabel}>
                IPD Billing — Bill: {fmtINR(ipdBillingTotal.bill)}   Disc: {fmtINR(ipdBillingTotal.disc)}   Net:
              </PDFText>
              <PDFText style={styles.sectionTotalAmt}>{fmtINR(ipdBillingTotal.net)}</PDFText>
            </PDFView>

            {/* FINAL CALCULATION SUMMARY */}
            <PDFView style={{ marginTop: 12, borderWidth: 1.5, borderColor: C.primaryDark, borderRadius: 4 }}>
              <PDFView style={{ flexDirection: 'row', backgroundColor: C.primaryDark, paddingVertical: 4, paddingHorizontal: 8 }}>
                <PDFText style={{ flex: 2, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white }}>FINAL CALCULATION</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right' }}>Bill Amt.</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right' }}>Discount</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right' }}>Net Amt.</PDFText>
              </PDFView>
              {[
                ['OPD Consultancy', opdConsultancyTotal],
                ['OPD Billing',     opdBillingTotal],
                ['IPD Billing',     ipdBillingTotal],
              ].map(([label, t], i) => (
                <PDFView key={label} style={{ flexDirection: 'row', paddingVertical: 3, paddingHorizontal: 8, backgroundColor: i % 2 === 0 ? C.white : C.altRow, borderBottomWidth: 0.3, borderBottomColor: C.border }}>
                  <PDFText style={{ flex: 2, fontSize: 7.5 }}>{label}</PDFText>
                  <PDFText style={{ flex: 1, fontSize: 7.5, textAlign: 'right' }}>{fmt(t.bill)}</PDFText>
                  <PDFText style={{ flex: 1, fontSize: 7.5, textAlign: 'right', color: C.red }}>{t.disc > 0 ? `(-) ${fmt(t.disc)}` : fmt(0)}</PDFText>
                  <PDFText style={{ flex: 1, fontSize: 7.5, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'right' }}>{fmt(t.net)}</PDFText>
                </PDFView>
              ))}
              <PDFView style={{ flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 8, backgroundColor: C.primaryDark, alignItems: 'center' }}>
                <PDFText style={{ flex: 2, fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: C.white }}>GRAND TOTAL (NET)</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right' }}>{fmt(grandBill)}</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: '#FF8A80', textAlign: 'right' }}>(-) {fmt(grandDisc)}</PDFText>
                <PDFText style={{ flex: 1, fontSize: 10, fontFamily: 'Roboto', fontWeight: 700, color: '#FFD54F', textAlign: 'right' }}>{fmtINR(grandNet)}</PDFText>
              </PDFView>
            </PDFView>
            <PDFText style={{ fontSize: 6, color: C.gray, marginTop: 2 }}>
              * Discount includes both service-item level discount and bill-level discount (bill-level counted once per bill; subtotals may exceed the sum of row-level item discounts).
            </PDFText>

            {/* FOOTER */}
            <PDFView style={styles.footer} fixed>
              <PDFText style={styles.footerLeft}>Doctor Ledger Summary — {doctorName}  |  Printed: {printTime}</PDFText>
              <PDFText style={styles.footerRight} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </PDFView>

          </PDFPage>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default DoctorLedgerSummaryReportPdf;
